import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import { selectProjectInfo, selectMacro1 } from '../../../../store/projectInfo/projectInfoSlice';
import './Mesh.scss';
import { IMeshProps } from './IMesh';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import { ErrorMessage, Field, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import Select, { SelectProps } from '@mui/material/Select';
import { setDisableRun, setMesh, setReadOnly} from '../../../store/project/projectslice';
import { RootState } from '../../../../../reduxService';
import { useEffect } from 'react';
import { updateValidation } from '../../../../Macro1/store/project/resultsSlice';
import { getMacro1MeshTypes } from '../../../../api/products/BeMaccaferriProductsHelper';
import { IMacro1MeshTypeResponse } from '../../../../api/IApiResponse';
import { conversionLabel, convertTo, EConversionSIToImperial } from '../../../../conversion';
import FieldNumber from '../../../../../components/FieldNumber/FieldNumber';

export interface IMeshForm {
    meshType: number;
    meshTypeName: string;
    nominalPunchingResistance: number;
}

export const MeshDefault: IMeshForm = {
    meshType: 0,
    meshTypeName: "",
    nominalPunchingResistance: 90,
};

const MuiSelect = (props: SelectProps) => (
    <Select {...props} />
);

const Mesh = (props: IMeshProps) => {
    const { t } = useTranslation();
    const [meshTypeList, setMeshTypeList] = React.useState<IMacro1MeshTypeResponse[]>([]);
    const projectInfo = useAppSelector(selectProjectInfo);
    const macro1 = useAppSelector(selectMacro1);

    const MeshValidationSchema = Yup.object().shape({
        meshType: Yup.number().min(1, `${t("yup.mixed.required")}`).required().label(t('mesh_type', { unit_measure: '' })),
        meshTypeName: Yup.string(),
        nominalPunchingResistance: Yup.number().label(t('mesh_nominal_punching_resistance', { unit_measure: '' })),
    });

    const dispatch = useAppDispatch();
    const dispatchMesh = (payload: IMeshForm) => {
        dispatch(setMesh(payload));
    }

    const formikProps = useFormik({
        initialValues: macro1.project?.data?.mesh ?? MeshDefault,
        validationSchema: MeshValidationSchema,
        onSubmit: (values, { setSubmitting }) => {
            setSubmitting(false);
        },
    });

    useEffect(() => {
        if( !!projectInfo.data?.unit){
            getMacro1MeshTypes().then((response) => {
                setMeshTypeList(response.data);
                const selectedMesh = response.data.find((mt) => mt.id == formikProps.values.meshType) ?? response.data.find(el => el.name == 'Steelgrid HR 30') ?? response.data[0];
                if (formikProps.values.meshType == 0 && selectedMesh) {
                    formikProps.setFieldValue('meshType', selectedMesh.id);
                    formikProps.setFieldValue('meshTypeName', selectedMesh.name);
                    formikProps.setFieldValue('nominalPunchingResistance', selectedMesh.nominalPunchingResistanceInLabTest);
                    //formikProps.setFieldValue('ultimateTensileStrengthPerLinearMeter', selectedMesh.ultimateTensileStrengthPerLinearMeter);
                } else if (macro1?.project?.data?.readOnly) {
                    formikProps.setFieldValue('meshType', 0);
                }
            }).catch((error) => {
                console.error("getMacro1MeshTypes", error);
            });
        }
    }, [projectInfo.data?.unit, window.localStorage.getItem("i18nextLng")]);

    const handleValueChanged = (valueKey: string, value: any) => {
        formikProps.setFieldValue(valueKey, value);
        if (valueKey == 'meshType') {
            const selectedMesh = meshTypeList.find((mt) => mt.id == value);
            if (selectedMesh) {
                formikProps.setFieldValue('meshTypeName', selectedMesh.name);
                formikProps.setFieldValue('nominalPunchingResistance', selectedMesh.nominalPunchingResistanceInLabTest);
                //formikProps.setFieldValue('ultimateTensileStrengthPerLinearMeter', selectedMesh.ultimateTensileStrengthPerLinearMeter);
            }
        }
        dispatch(setReadOnly(false));
        dispatch(setDisableRun(false));
    }

    useEffect(() => {
        dispatchMesh(formikProps.values);
    }, [formikProps.values]);

    const currentResults = useAppSelector((state: RootState) => {
        return { ...state.Macro1.results.data };
    });

    const getMeasurementUnit = (unit: string[]) => {
        return ' [' + unit[projectInfo.data?.unit == 'Metric' ? 0 : 1] + ']';
    }

    useEffect(() => {
        if (currentResults.validation?.startValidation && !currentResults.validation?.mesh) {
            formikProps.submitForm();
            formikProps.validateForm().then((errors) => {
                if (currentResults.validation)
                    dispatch(updateValidation({
                        ...currentResults.validation,
                        mesh: true,
                        meshErrors: Object.keys(errors).length > 0,
                    }));
            });

        }
    }, [JSON.stringify(currentResults.validation)]);


    return (
        <div className='style-form'>
            <FormikProvider value={formikProps}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <label>{t('mesh_type')}</label>
                        <Field
                            as={MuiSelect} name="meshType" value={formikProps.values.meshType}
                            onChange={(e: any) => handleValueChanged('meshType', e.target.value)}>
                            {meshTypeList.map((mt) => (
                                <MenuItem value={mt.id} key={mt.id}>{mt.name}</MenuItem>
                            ))}
                        </Field>
                        <ErrorMessage name="meshType" component="div" className="text-danger" />
                    </Grid>
                    <Grid item xs={11}>
                        <label>{t('mesh_nominal_punching_resistance', { unit_measure: getMeasurementUnit(conversionLabel.kN_lbf) })}</label>
                        <FieldNumber name="nominalPunchingResistance"  disabled/>
                        <ErrorMessage name="nominalPunchingResistance" component="div" className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className="symbol">{t("mesh_nominal_punching_resistance_symbol")}</label>
                    </div>
                </Grid>
            </FormikProvider>
        </div>
    );
};

export default Mesh;
